/**
 * RP4 — Stand-in renderer handle, font-dispatch, and bounds helpers
 * extracted from `Scene3DStage.tsx`.
 *
 * # Contents
 *
 *   - `makePlaceholderHandle` — PR-A renderer handle stub used by the
 *     test bridge while the bulkLoad path drives the real meshes.
 *   - Font-dispatch constants + `resolveDispatchFontName` — picks the
 *     SDF / SHX font for a given run.
 *   - `computeBoundsFromSpatialIndex` + `…ForIds` — pure AABB queries.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import { createSpatialIndex } from "../spatialIndex";
import type { RendererHandle, SpatialIndex } from "../types";
import type { ZoomTargetBounds } from "../cameraApi";
import { isShxFontName } from "../text/fontRegistry";
import {
  SDF_GOST_TYPE_A,
  SDF_GOST_TYPE_B,
  mapCisFontToAtlas,
} from "../text/cisFontAtlas";

/**
 * Default SDF atlas — used when an entity has no styleName or the
 * styleName resolves to neither a known SHX family nor a shipped
 * atlas. Every drawing always has at least one TEXT/MTEXT entity
 * referencing some font name, so this fallback ensures the renderer
 * never produces an empty mesh for a present-but-unknown style.
 */
export const PR_E5_DEFAULT_SDF_FONT = "Roboto-Regular";

/**
 * CIS engineering SDF atlas constants + the CIS→atlas mapping live in the
 * low-level, dependency-free `text/cisFontAtlas.ts` so BOTH the dispatch
 * resolver here AND `text/fontRegistry.resolveFont` (SHX-fail fallback)
 * can consume the same source of truth without an import cycle. Re-export
 * the constants + `mapCisFontToAtlas` so existing consumers / tests that
 * resolve them from `scenePlaceholder` keep working.
 */
export { SDF_GOST_TYPE_A, SDF_GOST_TYPE_B, mapCisFontToAtlas };

/**
 * Set of SDF atlas names actually shipped in the bundle. Used by
 * `resolveDispatchFontName` so a TTF-style request for a font we
 * don't have an atlas for falls back to the default SDF.
 *
 * Roboto-Regular is the Cyrillic-capable fallback; the OpenGost Type A /
 * Type B atlases cover CIS engineering (GOST 2.304) text styles. Further
 * atlases (ISOCPEUR, RomanT, ...) land as separate font-add tasks.
 */
const SDF_AVAILABLE_ATLASES = new Set<string>([
  PR_E5_DEFAULT_SDF_FONT,
  SDF_GOST_TYPE_A,
  SDF_GOST_TYPE_B,
]);

/**
 * Track font names we've already warned about so the console doesn't
 * fill with duplicate "missing font" messages on every redraw.
 */
const FONT_WARNED_NAMES = new Set<string>();

/**
 * PR-E5 — pick the dispatch font name for a given run / entity style.
 *
 *   - SHX-shaped name → returned VERBATIM (first, before the CIS map);
 *     the SHX path owns it so an UPLOADED `gost.shx` / `isocpeur.shx`
 *     (both `isShxFontName` true — `.shx` suffix or a `KNOWN_SHX_FAMILIES`
 *     root) renders the user's exact strokes. `resolveFont` still falls
 *     back to the bundled OpenGost atlas when that SHX is UNREGISTERED,
 *     so an SHX-shaped GOST request never goes blank.
 *   - NON-SHX CIS/GOST/ISOCPEUR style → bundled Cyrillic OpenGost atlas
 *     (`GOST-2.304_Type-B`, `ГОСТ`, … — the КЖ style names) so Russian
 *     drawing text renders real glyphs instead of the Latin-only fallback.
 *   - Available SDF atlas → returned verbatim.
 *   - Anything else → log once and fall back to Roboto-Regular so the
 *     entity still renders. The `MissingFontError` placeholder kicks
 *     in only when both the SHX FontRegistry and the SDF atlas dir
 *     are empty for the chosen name.
 *
 * @codex #334 — the CIS map used to run BEFORE `isShxFontName`, which
 * intercepted an SHX-shaped `gost.shx` / `isocpeur` (`isocp`/`isocpeur`
 * are `KNOWN_SHX_FAMILIES`) to the SDF atlas and NEVER let an uploaded
 * SHX win. The SHX check now runs first; only NON-SHX names reach the
 * CIS map.
 */
export function resolveDispatchFontName(requested: string | undefined): string {
  const raw = (requested ?? "").trim();
  if (raw === "") return PR_E5_DEFAULT_SDF_FONT;
  // Strip AutoCAD's width/oblique suffix (`…$001H0`) ONCE, up front, so every
  // downstream decision sees the bare font name. Critically this must precede
  // the SHX check: a suffixed CIS SHX like `gost.shx$001H0` / `isocpeur$001H0`
  // would otherwise miss `isShxFontName` (the `.shx$` test + KNOWN_SHX roots
  // don't see the `$…` tail) and get routed straight to the OpenGost atlas,
  // bypassing a user's UPLOADED SHX — violating the SHX-first contract (@codex
  // #334). The suffix is render metadata, not part of the font file name.
  const name = raw.split("$")[0]?.trim() ?? raw;
  if (name === "") return PR_E5_DEFAULT_SDF_FONT;
  // SHX-shaped request first — returned verbatim so the SHX path (which
  // checks FontRegistry for an UPLOADED font) wins. `resolveFont`'s
  // SHX-fail catch maps an unregistered CIS .shx to the OpenGost atlas, so
  // a GOST/ISOCPEUR .shx still renders real glyphs without being routed
  // away from a user's exact strokes here.
  if (isShxFontName(name)) return name;
  // NON-SHX CIS/GOST/ISOCPEUR styles — route to the bundled Cyrillic
  // OpenGost atlas (we ship the TTF atlas, not the proprietary .shx
  // bytes), so generically-spelled GOST/ISOCPEUR text always renders real
  // glyphs. КЖ style names like "GOST-2.304_Type-B" / "ГОСТ" land here.
  const gost = mapCisFontToAtlas(name);
  if (gost) return gost;
  if (SDF_AVAILABLE_ATLASES.has(name)) return name;
  if (!FONT_WARNED_NAMES.has(name)) {
    FONT_WARNED_NAMES.add(name);
    console.warn(
      `[scene3d.text] PR-E5: no SDF atlas / SHX font for '${name}'; falling back to Roboto-Regular.`,
    );
  }
  return PR_E5_DEFAULT_SDF_FONT;
}

/**
 * Stand-in `RendererHandle` for PR-A. Has a real spatial index (so
 * `entityCount()` returns 0 truthfully) but every other method is a
 * no-op. PR-B's mesh wiring lives in `<SceneContent>`; this handle is
 * still the surface that the test bridge talks to. PR-C / PR-G replace
 * the no-op selection / draft methods with real plumbing.
 */
export function makePlaceholderHandle(): RendererHandle {
  const spatialIndex = createSpatialIndex();
  return {
    spatialIndex,
    setEntities: () => {
      /* PR-B feeds entities directly into SceneContent; this hook is
         retained for the test bridge surface only. */
    },
    patchEntity: () => {
      /* PR-B+ */
    },
    removeEntity: () => {
      /* PR-B+ */
    },
    setSelection: () => {
      /* PR-C */
    },
    setHovered: () => {
      /* PR-C */
    },
    setDraftPreview: () => {
      /* PR-G */
    },
    setCamera: () => {
      /* PR-B+ */
    },
    worldFromClient: () => ({ x: 0, y: 0 }),
    clientFromWorld: () => ({ x: 0, y: 0 }),
    setLayerVisibility: () => {
      /* PR-I */
    },
    setActiveAnnotativeScale: () => {
      /* PR-I */
    },
    renderFrame: () => {
      /* PR-B+ */
    },
    resize: () => {
      /* PR-B+ */
    },
    dispose: () => {
      spatialIndex.clear();
    },
  };
}

/**
 * PR-V0b — compute the union AABB of every entity tracked by the
 * spatial index. Returns `null` if the index is empty / unavailable.
 *
 * Opt 3 — `intersect()` no longer carries bounds in its result.
 * Instead we walk `index.ids()` and pull each bbox via `getBounds(id)`
 * (O(1) hashmap read). Same O(N) wall cost as the previous huge-rect
 * intersect, without the rbush spatial walk.
 */
export function computeBoundsFromSpatialIndex(
  index: SpatialIndex | null,
): ZoomTargetBounds | null {
  if (!index) return null;
  if (index.size() === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = 0;
  for (const id of index.ids()) {
    const b = index.getBounds(id);
    if (!b) continue;
    found += 1;
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  if (
    found === 0 ||
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    return null;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * PR-V0b — compute the union AABB of a specific entity-id subset.
 * Returns `null` if no id resolves; ids that don't exist in the
 * index are silently skipped (they may have been removed since the
 * picker captured them).
 *
 * Opt 3 — direct id → bbox lookup via `getBounds(id)`. Avoids the
 * O(N) full-index scan the pre-Opt-3 path needed.
 */
export function computeBoundsFromSpatialIndexForIds(
  index: SpatialIndex | null,
  ids: readonly string[],
): ZoomTargetBounds | null {
  if (!index || ids.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = 0;
  for (const id of ids) {
    const b = index.getBounds(id);
    if (!b) continue;
    found += 1;
    if (b.minX < minX) minX = b.minX;
    if (b.minY < minY) minY = b.minY;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.maxY > maxY) maxY = b.maxY;
  }
  if (found === 0) return null;
  return { minX, minY, maxX, maxY };
}
